import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/useAuth";
import { TextScramble } from "@/components/effects/TextScramble";
import { MouseSpotlight } from "@/components/effects/MouseSpotlight";
import { usePortfolioProjects } from "@/hooks/usePortfolio";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface Project {
  id: string;
  name: string;
  description: string;
  previewUrl: string;
  galleryImages: string[];
}

const Hero = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const canAccessPortal = !!user && (user.isAdmin || user.isProvisionedClient);

  // Fetch portfolio projects from API
  const { data: portfolioData, isLoading: portfolioLoading } = usePortfolioProjects();

  // Map API response to Project interface
  const projects: Project[] = useMemo(() => {
    const portfolioProjects = portfolioData?.projects ?? [];
    return portfolioProjects.map((p) => ({
      id: p.id,
      name: p.name,
      description: p.description,
      previewUrl: p.coverImageUrl,
      galleryImages: p.galleryImages || [p.coverImageUrl],
    }));
  }, [portfolioData]);

  // Duplicate projects for infinite scroll effect
  const duplicatedProjects = useMemo(() => {
    if (projects.length === 0) return [];
    // Need enough copies for smooth infinite scroll
    const copies = Math.max(4, Math.ceil(8 / projects.length));
    return Array(copies).fill(projects).flat();
  }, [projects]);

  // Preload all gallery images when projects load
  useEffect(() => {
    projects.forEach((project) => {
      project.galleryImages.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    });
  }, [projects]);

  const handleBookCall = () => {
    if (canAccessPortal) {
      navigate("/portal");
    } else {
      navigate("/contact");
    }
  };

  return (
    <MouseSpotlight className="relative flex items-center justify-center">
      {/* Mesh gradient background */}
      <div className="absolute inset-0 bg-mesh-gradient" />
      
      {/* Animated stars/particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-primary/30 rounded-full animate-pulse-slow"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${3 + Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      {/* Horizon glow line */}
      <div className="absolute bottom-0 left-0 right-0 h-px">
        <div className="horizon-glow animate-horizon-pulse" />
      </div>

      <section className="relative pt-40 pb-16 px-4 z-10 w-full">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-32 items-center">
            {/* Left: Text Content */}
            <div className="text-center lg:text-left">

              {/* Headline with text scramble */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8 leading-[1.1] animate-fade-in font-heading">
                <TextScramble 
                  text="We build your ideas" 
                  delay={100}
                  scrambleDuration={600}
                />
                <br />
                <span className="text-gradient-indigo">
                  <TextScramble 
                    text="from zero to scale" 
                    delay={400}
                    scrambleDuration={500}
                  />
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-14 animate-fade-in leading-relaxed" style={{ animationDelay: '0.3s' }}>
                The technical team for ambitious entrepreneurs. We design, build, and scale your product - you focus on vision.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 animate-fade-in" style={{ animationDelay: '0.5s' }}>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="gap-2 border-foreground/20 hover:bg-foreground/5" 
                  onClick={handleBookCall} 
                  disabled={loading}
                >
                  {canAccessPortal ? "Go to Portal" : "Book a Call"}
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="lg"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  View Pricing
                </Button>
              </div>
            </div>

            {/* Right: Work Showcase Carousel */}
            <div className="relative animate-fade-in" style={{ animationDelay: '0.4s' }}>
              {/* Section Label */}
              <div className="hidden lg:block text-center mb-6">
                <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                  OUR WORK
                </p>
                <p className="text-xs text-muted-foreground/60 mt-1">
                  Click to explore
                </p>
              </div>
              
              <div className="relative h-[400px] lg:h-[450px] overflow-hidden">
                {/* Fade edges */}
                <div className="absolute left-0 right-0 top-0 h-20 bg-gradient-to-b from-background to-transparent z-10 pointer-events-none" />
                <div className="absolute left-0 right-0 bottom-0 h-20 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />

                {/* Loading state */}
                {portfolioLoading && (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                )}

                {/* Empty state */}
                {!portfolioLoading && projects.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground text-sm">Projects coming soon...</p>
                  </div>
                )}

                {/* Vertical scrolling container */}
                {!portfolioLoading && projects.length > 0 && (
                  <div className="animate-scroll-vertical">
                    {[...duplicatedProjects, ...duplicatedProjects].map((project, index) => (
                      <div
                        key={`vertical-${project.id}-${index}`}
                        onClick={() => setSelectedProject(project)}
                        className="group cursor-pointer mb-6 px-4"
                      >
                        <div
                          className={`
                            glass-panel rounded-2xl overflow-hidden transition-all duration-500
                            group-hover:glow-border group-hover:scale-[1.02]
                            ${index % 2 === 0 ? 'ml-4' : 'mr-4'}
                          `}
                        >
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={project.previewUrl}
                              alt={project.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                        </div>
                        <p className="text-center mt-3 font-medium text-foreground/80 group-hover:text-foreground transition-colors text-sm">
                          {project.name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-glow rounded-full blur-3xl -z-10 opacity-40" />

      {/* Project Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-3xl glass-panel border-border/50">
          {selectedProject && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading">
                  {selectedProject.name}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {selectedProject.description}
                </DialogDescription>
              </DialogHeader>

              {/* Image Gallery */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 my-4 max-h-[400px] overflow-y-auto">
                {selectedProject.galleryImages.map((img, index) => (
                  <div 
                    key={index} 
                    className={`rounded-lg overflow-hidden border border-border/30 ${index === 0 ? 'col-span-2 md:col-span-2' : ''}`}
                  >
                    <img
                      src={img}
                      alt={`${selectedProject.name} screenshot ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </MouseSpotlight>
  );
};

export default Hero;
